// georef.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';
import { Country } from '@models/pais/paisDto.model';


@Injectable({
  providedIn: 'root',
})
export class GeorefService {
  private baseUrl = 'https://apis.datos.gob.ar/georef/api';
  private countriesAmericasUrl = 'https://restcountries.com/v3.1/region/americas';

  private divisionesPorPais: { [codigoPais: string]: string } = {
    'ARG': 'provincias',
    'BRA': 'estados',
    'COL': 'departamentos',
    'CHL': 'regiones',
    'MEX': 'estados',
    'USA': 'states',
    'CAN': 'provincias',
    // Agregá más según necesidad
  };

  constructor(private http: HttpClient) { }

  getProvincias(): Observable<any> {
    return this.http.get(`${this.baseUrl}/provincias`);
  }

  getProvinciaIdPorLocalidadId(idLocalidad: number): Observable<string | null> {
  const url = `${this.baseUrl}/localidades?id=${idLocalidad}&campos=id,nombre,provincia&max=1`;
  return this.http.get<any>(url).pipe(
    map(response => response.localidades[0]?.provincia?.id || null)
  );
}

  getLocalidadesPorProvincia(nombreProvincia: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/localidades`, {
      params: {
        provincia: nombreProvincia,
        campos: 'id,nombre',
        max: 500,
      },
    });
  }

  getLocalidadPorId(idLocalidad: number): Observable<any> {
    const url = `${this.baseUrl}/localidades?id=${idLocalidad}&campos=id,nombre,provincia&max=1`;
    return this.http.get<any>(url).pipe(
      map(response => response.localidades[0] || null)
    );
  }

  getCountriesObservable(): Observable<Country[]> {
    return this.http.get<Country[]>(this.countriesAmericasUrl).pipe(
      catchError(error => {
        console.error('Error fetching countries', error);
        return of([]); // Retorna un array vacío en caso de error
      })
    );
  }

  getAllNacionalidades(): Observable<{ name: string; nationality: string }[]> {
    return this.http.get<any[]>('https://restcountries.com/v3.1/all').pipe(
      map(countries =>
        countries.map(country => ({
          name: country.name.common,
          nationality: country.demonyms?.eng?.m || 'N/A'
        }))
      ),
      catchError(error => {
        console.error('Error fetching countries', error);
        return of([]);
      })
    );
  }

  getSouthAmericanCountries(): Observable<{ name: string; flag: string; divisionType: string }[]> {
    const divisionMap: { [key: string]: string } = {
      Argentina: 'Provincias',
      Bolivia: 'Departamentos',
      Brazil: 'Estados',
      Chile: 'Regiones',
      Colombia: 'Departamentos',
      Ecuador: 'Provincias',
      Guyana: 'Regiones',
      Paraguay: 'Departamentos',
      Peru: 'Regiones',
      Suriname: 'Distritos',
      Uruguay: 'Departamentos',
      Venezuela: 'Estados',
    };

    return this.http.get<any[]>(this.countriesAmericasUrl).pipe(
      map(countries =>
        countries
          .filter(country => country.subregion === 'South America')
          .map(country => ({
            name: country.name.common || country.name,
            flag: country.flags?.svg || country.flags?.png || '',
            divisionType: divisionMap[country.name.common || country.name] || 'División'
          }))
          .sort((a, b) => a.name.localeCompare(b.name))
      )
    );
  }

  getProvinciasPorPais(codigoPais: string): Observable<any> {
    if (codigoPais === 'ARG') {
      return this.http.get(`${this.baseUrl}/provincias`).pipe(
        map((res: any) => res.provincias)
      );
    }
    // Aquí podés agregar otra lógica para otros países o devolver vacío
    return of([]);
  }

  getDivisionAdministrativaPorPais(codigoPais: string): string {
    return this.divisionesPorPais[codigoPais] || 'división desconocida';
  }

}
